import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FileSpreadsheet, RefreshCw } from 'lucide-react';
import { Botao, Cartao, Segmentado, Selo, type CorSelo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { CampoData, Rotulo, Selecao } from '../../componentes/formulario';
import { DialogoImportacao } from '../../componentes/importacao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post, textoDeErroGravado } from '../../nucleo/api';
import { data, dataHora, dinheiro, documentoFormatado, mesAno, nomePessoa, numero } from '../../nucleo/formato';
import { useSessao, veTodasAsEmpresas } from '../../nucleo/sessao';
import { CampoEmpresaDaTela, EmpresaDaTelaContexto, useEmpresaDaTela, useEmpresaDaTelaContexto } from '../../nucleo/empresa/EmpresaDaTela';
import type { ClasseComparacao, ComparacaoContaAzul, ConversaoContaAzul, EntidadeConversao, ItemComparacaoContaAzul, ResumoContaAzul, TotaisContaAzul } from './tipos';

/**
 * Trilha Conta Azul (ficha 2026-10-13_malgo_MIG_conta-azul): Integrações > Conta Azul. Só leitura da Conta Azul: estado da
 * leitura para o espelho, mapa centro de custo → empresa (conector geral), comparação com o MalgoOS (só lá, só aqui,
 * divergente; totais mensais lado a lado) e o conversor para os importadores existentes, com a prévia do próprio importador.
 * Os componentes e as classes são os das telas existentes (cartão, selo, segmentado, tabela dos relatórios).
 */

const TH = 'px-4 py-2.5 font-medium whitespace-nowrap';
const TD = 'px-4 py-2.5';
const CHAVE = ['integracoes', 'conta-azul'] as const;

/**
 * Rodada rval1-1 (RVAL1-ROB-M2): com a troca de empresa desligada, a empresa da tela (o campo "Empresa" do topo dela, padrão
 * "Minhas verticais") vai no `X-Empresa` das leituras, da sincronização e da importação. Com a troca ligada, vale o filtro do topo.
 */
function useBuscar() {
  const { empresa, cabecalho } = useEmpresaDaTelaContexto();
  return { empresa, cabecalho, buscar: <T,>(caminho: string) => api<T>('GET', caminho, undefined, { headers: cabecalho }) };
}

function Rolagem({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div tabIndex={0} role="region" aria-label={rotulo} className="relative overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
      {children}
    </div>
  );
}

const COR_CLASSE: Record<ClasseComparacao, CorSelo> = { so_la: 'laranja', so_aqui: 'ciano', divergente: 'perigo', igual: 'verde' };
const hojeMes = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);

// -------------------------------------------------------------------------------------------- leitura (sincronização)

function CartaoLeitura({ r }: { r: ResumoContaAzul }) {
  const { t } = useTranslation('integracoes');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [jobId, setJobId] = useState<string | null>(null);
  const { cabecalho } = useBuscar();
  const pode = s.pode('integracoes.conectores');
  const job = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => get<{ estado: string; erro: string | null; resultado: { erro: string | null } | null }>(`/jobs/${jobId}`),
    enabled: !!jobId,
    refetchInterval: (q) => (q.state.data && ['concluido', 'erro'].includes(q.state.data.estado) ? false : 2000),
  });
  useEffect(() => {
    const e = job.data?.estado;
    if (!jobId || (e !== 'concluido' && e !== 'erro')) return;
    const falha = e === 'erro' ? job.data!.erro : (job.data!.resultado?.erro ?? null);
    if (falha) toast.erro(t('contaAzul.leitura.falhou', { erro: textoDeErroGravado(falha) }));
    else toast.sucesso(t('contaAzul.leitura.concluida'));
    setJobId(null);
    void qc.invalidateQueries({ queryKey: CHAVE });
  }, [job.data?.estado]); // eslint-disable-line react-hooks/exhaustive-deps
  const sincronizar = useMutation({
    mutationFn: (completa: boolean) => post<{ job_id: string }>('/integracoes/conta-azul/sincronizar', { completa }, cabecalho),
    onSuccess: (x) => {
      setJobId(x.job_id);
      toast.info(t('contaAzul.leitura.enfileirada'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const conectado = r.conector?.estado === 'conectado';
  const lendo = sincronizar.isPending || !!jobId;
  const n = r.espelho;
  return (
    <Cartao
      titulo={t('contaAzul.leitura.titulo')}
      acao={
        pode && conectado ? (
          <div className="flex flex-wrap gap-2">
            <Botao icone={<RefreshCw className="size-4" aria-hidden />} carregando={lendo && sincronizar.variables !== true} disabled={lendo} onClick={() => sincronizar.mutate(false)}>
              {t('contaAzul.leitura.agora')}
            </Botao>
            <Botao variante="fantasma" carregando={lendo && sincronizar.variables === true} disabled={lendo} onClick={() => sincronizar.mutate(true)}>
              {t('contaAzul.leitura.completa')}
            </Botao>
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        {!r.conector ? (
          <p className="text-tinta-2">
            {t('contaAzul.leitura.semConector')}{' '}
            <Link to="/integracoes/integracoes/conectores" className="-my-1 inline-block py-1 font-medium text-acento-forte hover:underline">
              {t('contaAzul.leitura.irConectores')}
            </Link>
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <Selo cor={conectado ? 'verde' : r.conector.estado === 'desconectado' ? 'neutro' : 'perigo'}>{t(`estado.${r.conector.estado}`)}</Selo>
            {r.conector.empresa && <Selo cor="ciano">{t('conectores.empresa.selo')}</Selo>}
            {r.conector.ambiente === 'duble' && <Selo cor="laranja">{t('contaAzul.leitura.duble')}</Selo>}
          </div>
        )}
        <p className="text-xs text-tinta-3">{t('contaAzul.leitura.soLeitura')}</p>
        <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2 xl:grid-cols-4">
          {(
            [
              ['pessoas', n.pessoas],
              ['contratos', n.contratos],
              ['titulos_receber', n.titulos_receber],
              ['titulos_pagar', n.titulos_pagar],
              ['sem_empresa', n.sem_empresa],
              ['sem_detalhe', n.sem_detalhe],
              ['removidos', n.removidos],
            ] as const
          ).map(([k, v]) => (
            <div key={k} className="flex min-w-0 justify-between gap-2">
              <dt className="text-tinta-3">{t(`contaAzul.espelho.${k}`)}</dt>
              <dd className="numero text-tinta-2">{numero(v)}</dd>
            </div>
          ))}
        </dl>
        {r.sincronias.length > 0 && (
          <Rolagem rotulo={t('contaAzul.leitura.rodadas')}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-borda text-left text-tinta-3">
                  <th scope="col" className={TH}>{t('contaAzul.leitura.recurso')}</th>
                  <th scope="col" className={TH}>{t('contaAzul.leitura.ultima')}</th>
                  <th scope="col" className={TH}>{t('contaAzul.leitura.ultimaCompleta')}</th>
                  <th scope="col" className={`${TH} text-right`}>{t('contaAzul.leitura.lidos')}</th>
                  <th scope="col" className={TH}>{t('contaAzul.leitura.erro')}</th>
                </tr>
              </thead>
              <tbody>
                {r.sincronias.map((x) => (
                  <tr key={`${x.escopo}:${x.recurso}`} className="border-b border-borda last:border-0">
                    <th scope="row" className={`${TD} text-left font-normal`}>{t(`contaAzul.recursos.${x.recurso}`)}</th>
                    <td className={`${TD} numero whitespace-nowrap`}>{dataHora(x.executado_em)}</td>
                    <td className={`${TD} numero whitespace-nowrap`}>{dataHora(x.completa_em)}</td>
                    <td className={`${TD} numero text-right`}>{numero(x.lidos)}</td>
                    <td className={`${TD} break-words text-perigo`}>{x.erro ? textoDeErroGravado(x.erro) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Rolagem>
        )}
      </div>
    </Cartao>
  );
}

// -------------------------------------------------------------------------------------------- mapa centro → empresa

function MapaCentros({ r }: { r: ResumoContaAzul }) {
  const { t } = useTranslation('integracoes');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [mapa, setMapa] = useState<Record<string, string>>(() => Object.fromEntries(r.centros.map((c) => [c.id_externo, c.empresa_destino_id ?? ''])));
  const { empresa: daTela } = useBuscar();
  const emTodas = !(s.empresas?.atual ?? daTela);
  const pode = s.pode('integracoes.conectores') && veTodasAsEmpresas(s);
  const salvar = useMutation({
    mutationFn: () => api<{ centros: number; titulos: number }>('PUT', '/integracoes/conta-azul/centros', { mapa: r.centros.map((c) => ({ id_externo: c.id_externo, empresa_id: mapa[c.id_externo] || null })) }),
    onSuccess: async (x) => {
      toast.sucesso(t('contaAzul.centros.salvo', { count: x.titulos }));
      await qc.invalidateQueries({ queryKey: CHAVE });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const opcoes = r.empresas.map((e) => ({ valor: e.id, rotulo: e.nome }));
  return (
    <Cartao
      titulo={t('contaAzul.centros.titulo')}
      acao={
        pode ? (
          <Botao disabled={!emTodas} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {t('contaAzul.centros.salvar')}
          </Botao>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-tinta-3">{t('contaAzul.centros.texto')}</p>
        {pode && !emTodas && <p className="rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-2">{t('contaAzul.centros.escolhaTodas')}</p>}
        <Rolagem rotulo={t('contaAzul.centros.titulo')}>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-borda text-left text-tinta-3">
                <th scope="col" className={TH}>{t('contaAzul.centros.centro')}</th>
                <th scope="col" className={TH}>{t('contaAzul.centros.empresa')}</th>
              </tr>
            </thead>
            <tbody>
              {r.centros.map((c) => (
                <tr key={c.id_externo} className="border-b border-borda last:border-0">
                  <th scope="row" className={`${TD} text-left font-normal break-words`}>
                    {c.nome}
                    {c.codigo ? <span className="numero ml-2 text-xs text-tinta-3">{c.codigo}</span> : null}
                  </th>
                  <td className={`${TD} min-w-56`}>
                    <Selecao
                      aria-label={t('contaAzul.centros.empresaDe', { centro: c.nome })}
                      disabled={!pode}
                      vazio={t('contaAzul.centros.semMapa')}
                      opcoes={opcoes}
                      value={mapa[c.id_externo] ?? ''}
                      onChange={(e) => setMapa((m) => ({ ...m, [c.id_externo]: e.target.value }))}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Rolagem>
      </div>
    </Cartao>
  );
}

// -------------------------------------------------------------------------------------------- comparação

function LadoTitulo({ l, lado }: { l: ItemComparacaoContaAzul['ca'] | ItemComparacaoContaAzul['malgo']; lado: 'ca' | 'malgo' }) {
  const { t } = useTranslation('integracoes');
  if (!l) return <span className="text-tinta-3">{t('contaAzul.comparacao.ausente')}</span>;
  const nome = 'pessoa_nome' in l ? l.pessoa_nome : 'parceiro_nome' in l ? l.parceiro_nome : (l.nome ?? null);
  const situacao = 'status' in l ? l.status : 'estado' in l ? l.estado : null;
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="break-words">{nomePessoa(nome ?? '') || t('contaAzul.comparacao.semNome')}</span>
      {l.documento_digitos && <span className="numero text-xs text-tinta-3">{documentoFormatado(l.documento_digitos)}</span>}
      {l.vencimento && (
        <span className="numero text-xs text-tinta-2">
          {data(l.vencimento)} · {dinheiro(l.valor_centavos ?? 0)}
        </span>
      )}
      {situacao && <span className="text-xs text-tinta-3">{lado === 'ca' ? t(`contaAzul.situacoesCa.${situacao}`, { defaultValue: situacao }) : t(`contaAzul.situacoes.${situacao}`, { defaultValue: situacao })}</span>}
      {lado === 'malgo' && 'numero' in l && l.numero && <span className="numero text-xs text-tinta-3">{l.numero}</span>}
    </div>
  );
}

function Comparacao() {
  const { t } = useTranslation('integracoes');
  const [entidade, setEntidade] = useState<'titulos' | 'pessoas'>('titulos');
  const [tipo, setTipo] = useState<'receber' | 'pagar'>('receber');
  const [mes, setMes] = useState(hojeMes);
  const [classe, setClasse] = useState<ClasseComparacao | 'diferentes'>('diferentes');
  const { empresa, buscar } = useBuscar();
  const params = new URLSearchParams({ entidade, tipo, classe, ...(entidade === 'titulos' ? { mes } : {}) });
  const q = useQuery({
    queryKey: [...CHAVE, 'comparacao', entidade, tipo, mes, classe, ...(empresa ? [empresa] : [])],
    queryFn: () => buscar<ComparacaoContaAzul>(`/integracoes/conta-azul/comparacao?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
  return (
    <Cartao titulo={t('contaAzul.comparacao.titulo')}>
      <div className="flex flex-col gap-4 text-sm">
        <p className="text-tinta-3">{t('contaAzul.comparacao.texto')}</p>
        <div className="flex flex-wrap items-end gap-3">
          <Segmentado
            rotulo={t('contaAzul.comparacao.entidade')}
            valor={entidade}
            aoMudar={setEntidade}
            opcoes={[
              { valor: 'titulos', rotulo: t('contaAzul.comparacao.titulos') },
              { valor: 'pessoas', rotulo: t('contaAzul.comparacao.pessoas') },
            ]}
          />
          {entidade === 'titulos' && (
            <>
              <Rotulo id="ca-cmp-tipo" rotulo={t('contaAzul.comparacao.tipo')}>
                <Selecao
                  id="ca-cmp-tipo"
                  value={tipo}
                  onChange={(e) => setTipo(e.target.value as 'receber' | 'pagar')}
                  opcoes={[
                    { valor: 'receber', rotulo: t('contaAzul.tipos.receber') },
                    { valor: 'pagar', rotulo: t('contaAzul.tipos.pagar') },
                  ]}
                />
              </Rotulo>
              <Rotulo id="ca-cmp-mes" rotulo={t('contaAzul.comparacao.mes')}>
                <CampoData id="ca-cmp-mes" type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} />
              </Rotulo>
            </>
          )}
          <Rotulo id="ca-cmp-classe" rotulo={t('contaAzul.comparacao.mostrar')}>
            <Selecao
              id="ca-cmp-classe"
              value={classe}
              onChange={(e) => setClasse(e.target.value as ClasseComparacao | 'diferentes')}
              opcoes={(['diferentes', 'so_la', 'so_aqui', 'divergente', 'igual'] as const).map((c) => ({ valor: c, rotulo: t(`contaAzul.classes.${c}`) }))}
            />
          </Rotulo>
        </div>
        <Estado consulta={q}>
          {(c) => (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-2" data-contagens-conta-azul="">
                {(['so_la', 'so_aqui', 'divergente', 'igual'] as const).map((k) => (
                  <Selo key={k} cor={COR_CLASSE[k]}>
                    {t(`contaAzul.classes.${k}`)}: {numero(c.contagens[k])}
                  </Selo>
                ))}
              </div>
              {!c.acesso_malgo && <p className="rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-2">{t('contaAzul.comparacao.semAcesso')}</p>}
              {c.incompleto && (
                <p role="status" className="rounded-lg border border-laranja px-3 py-2 text-xs text-tinta-2">
                  {t('contaAzul.comparacao.incompleto', { teto: numero(20000) })}
                </p>
              )}
              {c.itens.length === 0 ? (
                <p className="text-tinta-3">{t('contaAzul.comparacao.vazio')}</p>
              ) : (
                <Rolagem rotulo={t('contaAzul.comparacao.titulo')}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-borda text-left text-tinta-3">
                        <th scope="col" className={TH}>{t('contaAzul.comparacao.classe')}</th>
                        <th scope="col" className={TH}>{t('contaAzul.comparacao.contaAzul')}</th>
                        <th scope="col" className={TH}>{t('contaAzul.comparacao.malgo')}</th>
                        <th scope="col" className={TH}>{t('contaAzul.comparacao.diferencas')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.itens.map((i, k) => (
                        <tr key={`${i.ca?.id_externo ?? ''}:${i.malgo?.id ?? ''}:${k}`} className="border-b border-borda align-top last:border-0">
                          <th scope="row" className={`${TD} text-left font-normal`}>
                            <span className="inline-flex min-w-min">
                              <Selo cor={COR_CLASSE[i.classe]} quebra>
                                {t(`contaAzul.classes.${i.classe}`)}
                              </Selo>
                            </span>
                            {i.ca?.empresa_inferida && <span className="mt-1 block text-xs text-laranja">{t('contaAzul.comparacao.semEmpresa')}</span>}
                          </th>
                          <td className={`${TD} min-w-48`}>
                            <LadoTitulo l={i.ca} lado="ca" />
                          </td>
                          <td className={`${TD} min-w-48`}>
                            <LadoTitulo l={i.malgo} lado="malgo" />
                          </td>
                          <td className={`${TD} text-xs text-tinta-2`}>
                            {[...i.diferencas.map((d) => t(`contaAzul.diferencas.${d}`)), i.ligacao === 'documento' ? t('contaAzul.comparacao.peloDocumento') : null].filter(Boolean).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Rolagem>
              )}
              {c.truncado && <p className="text-xs text-tinta-3">{t('contaAzul.comparacao.truncado')}</p>}
            </div>
          )}
        </Estado>
      </div>
    </Cartao>
  );
}

// -------------------------------------------------------------------------------------------- totais mensais

function Totais() {
  const { t } = useTranslation('integracoes');
  const anoAtual = Number(hojeMes().slice(0, 4));
  const [ano, setAno] = useState(anoAtual);
  const [tipo, setTipo] = useState<'receber' | 'pagar'>('receber');
  const { empresa, buscar } = useBuscar();
  const q = useQuery({
    queryKey: [...CHAVE, 'totais', ano, tipo, ...(empresa ? [empresa] : [])],
    queryFn: () => buscar<TotaisContaAzul>(`/integracoes/conta-azul/totais?ano=${ano}&tipo=${tipo}`),
    placeholderData: keepPreviousData,
  });
  const anos = Array.from({ length: anoAtual + 4 - 2021 }, (_, i) => 2021 + i);
  return (
    <Cartao titulo={t('contaAzul.totais.titulo')}>
      <div className="flex flex-col gap-4 text-sm">
        <div className="flex flex-wrap items-end gap-3">
          <Rotulo id="ca-tot-ano" rotulo={t('contaAzul.totais.ano')}>
            <Selecao id="ca-tot-ano" value={String(ano)} onChange={(e) => setAno(Number(e.target.value))} opcoes={anos.map((a) => ({ valor: String(a), rotulo: String(a) }))} />
          </Rotulo>
          <Rotulo id="ca-tot-tipo" rotulo={t('contaAzul.comparacao.tipo')}>
            <Selecao
              id="ca-tot-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as 'receber' | 'pagar')}
              opcoes={[
                { valor: 'receber', rotulo: t('contaAzul.tipos.receber') },
                { valor: 'pagar', rotulo: t('contaAzul.tipos.pagar') },
              ]}
            />
          </Rotulo>
        </div>
        <Estado consulta={q}>
          {(d) => (
            <Rolagem rotulo={t('contaAzul.totais.titulo')}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-borda text-left text-tinta-3">
                    <th scope="col" className={TH}>{t('contaAzul.totais.mes')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.caQtd')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.caValor')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.caAberto')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.malgoQtd')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.malgoValor')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.malgoAberto')}</th>
                    <th scope="col" className={`${TH} text-right`}>{t('contaAzul.totais.diferenca')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.meses.map((m) => {
                    const dif = m.malgo_valor === null ? null : m.ca_valor - m.malgo_valor;
                    return (
                      <tr key={m.mes} className="border-b border-borda last:border-0">
                        <th scope="row" className={`${TD} numero text-left font-normal`}>{mesAno(m.mes)}</th>
                        <td className={`${TD} numero text-right`}>{numero(m.ca_quantidade)}</td>
                        <td className={`${TD} numero text-right whitespace-nowrap`}>{dinheiro(m.ca_valor)}</td>
                        <td className={`${TD} numero text-right whitespace-nowrap`}>{dinheiro(m.ca_aberto)}</td>
                        <td className={`${TD} numero text-right`}>{m.malgo_quantidade === null ? '' : numero(m.malgo_quantidade)}</td>
                        <td className={`${TD} numero text-right whitespace-nowrap`}>{m.malgo_valor === null ? '' : dinheiro(m.malgo_valor)}</td>
                        <td className={`${TD} numero text-right whitespace-nowrap`}>{m.malgo_aberto === null ? '' : dinheiro(m.malgo_aberto)}</td>
                        <td className={`${TD} numero text-right whitespace-nowrap ${dif ? 'text-perigo' : ''}`}>{dif === null ? '' : dinheiro(dif)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Rolagem>
          )}
        </Estado>
        {q.data && !q.data.acesso_malgo && <p className="text-xs text-tinta-3">{t('contaAzul.comparacao.semAcesso')}</p>}
      </div>
    </Cartao>
  );
}

// -------------------------------------------------------------------------------------------- conversor

const ENTIDADES: EntidadeConversao[] = ['contas_financeiras', 'pessoas', 'contratos', 'titulos_receber', 'titulos_pagar'];

function LinhaConversor({ entidade, aoAbrir }: { entidade: EntidadeConversao; aoAbrir: (c: ConversaoContaAzul) => void }) {
  const { t } = useTranslation('integracoes');
  const toast = useToast();
  const [conv, setConv] = useState<ConversaoContaAzul | null>(null);
  const { buscar } = useBuscar();
  const gerar = useMutation({
    mutationFn: () => buscar<ConversaoContaAzul>(`/integracoes/conta-azul/converter?entidade=${entidade}`),
    onSuccess: setConv,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const avisos = conv ? Object.entries(conv.avisos).filter(([, n]) => n > 0) : [];
  return (
    <li className="flex flex-col gap-2 border-b border-borda py-3 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium">{t(`contaAzul.conversor.entidades.${entidade}`)}</span>
        <div className="flex flex-wrap gap-2">
          <Botao icone={<FileSpreadsheet className="size-4" aria-hidden />} carregando={gerar.isPending} onClick={() => gerar.mutate()}>
            {conv ? t('contaAzul.conversor.gerarDeNovo') : t('contaAzul.conversor.gerar')}
          </Botao>
          {conv && conv.linhas > 0 && <Botao onClick={() => aoAbrir(conv)}>{t('contaAzul.conversor.conferir')}</Botao>}
        </div>
      </div>
      {conv && (
        <div className="flex flex-col gap-1 text-xs text-tinta-2" role="status">
          {!conv.empresa_escolhida ? (
            <p>{t('contaAzul.conversor.empresaDoSeletor')}</p>
          ) : !conv.pode_importar ? (
            <p>{t('contaAzul.conversor.semPermissao')}</p>
          ) : (
            // Review r54 visual (B4): plural pela quantidade de linhas e os números pelo `numero()` (separador de milhar).
            // Review r55 visual (B4): e o dos restantes à parte ("1 fica para o próximo").
            <p>
              {t('contaAzul.conversor.resumoTudo', { count: conv.linhas, linhas: numero(conv.linhas) })}
              {conv.restantes > 0 && ` ${t('contaAzul.conversor.restantes', { count: conv.restantes, restantes: numero(conv.restantes) })}`}
            </p>
          )}
          {avisos.length > 0 && (
            <ul className="flex flex-wrap gap-1.5">
              {avisos.map(([k, n]) => (
                <li key={k}>
                  <Selo cor="laranja">{t(`contaAzul.conversor.avisos.${k}`, { count: n, defaultValue: `${k}: ${n}` })}</Selo>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </li>
  );
}

function Conversor() {
  const { t } = useTranslation('integracoes');
  const qc = useQueryClient();
  const [aberto, setAberto] = useState<ConversaoContaAzul | null>(null);
  const { empresa, cabecalho } = useBuscar();
  return (
    <Cartao titulo={t('contaAzul.conversor.titulo')}>
      <div className="flex flex-col gap-2 text-sm">
        <p className="text-tinta-3">{t('contaAzul.conversor.texto')}</p>
        <ul className="flex flex-col">
          {ENTIDADES.map((e) => (
            <LinhaConversor key={`${e}:${empresa ?? ''}`} entidade={e} aoAbrir={setAberto} />
          ))}
        </ul>
      </div>
      <DialogoImportacao
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        caminho={aberto?.importador.caminho ?? '/cadastros/parceiros'}
        titulo={aberto ? t('contaAzul.conversor.dialogo', { entidade: t(`contaAzul.conversor.entidades.${aberto.entidade}`) }) : ''}
        csvInicial={aberto ? { csv: aberto.csv, nome: `conta-azul-${aberto.entidade}.csv` } : null}
        cabecalhos={cabecalho}
        aoConcluir={() => void qc.invalidateQueries({ queryKey: CHAVE })}
      />
    </Cartao>
  );
}

export function ContaAzul() {
  const { t } = useTranslation('integracoes');
  const daTela = useEmpresaDaTela({ comTodas: true });
  const empresa = daTela.mostra ? daTela.empresa : null;
  const q = useQuery({ queryKey: daTela.mostra ? [...CHAVE, 'resumo', empresa ?? ''] : [...CHAVE, 'resumo'], queryFn: () => api<ResumoContaAzul>('GET', '/integracoes/conta-azul/resumo', undefined, { headers: daTela.cabecalho }) });
  return (
    <EmpresaDaTelaContexto.Provider value={{ empresa, cabecalho: daTela.cabecalho }}>
    <div className="flex flex-col gap-4">
      <p className="max-w-3xl text-sm text-tinta-3">{t('contaAzul.intro')}</p>
      <CampoEmpresaDaTela id="conta-azul-empresa" estado={daTela} />
      <Estado consulta={q}>
        {(r) => (
          <>
            <CartaoLeitura r={r} />
            {r.ve_geral && r.centros.length > 0 && <MapaCentros key={r.centros.map((c) => `${c.id_externo}:${c.empresa_destino_id ?? ''}`).join('|')} r={r} />}
            <Comparacao />
            <Totais />
            <Conversor />
          </>
        )}
      </Estado>
    </div>
    </EmpresaDaTelaContexto.Provider>
  );
}
